import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  INDUSTRIES,
  INDUSTRY_CONTENT,
  METHODOLOGY,
  OFFERS,
  trackEvent } from
"@/mock";

import { analyticsApi, siteCopyApi } from "@/services/phase1Api";
import ClawAssistantWidget from "@/components/ClawAssistantWidget";
import SplashVideoOverlay from "@/components/SplashVideoOverlay";
import ClarityWordmark from "@/components/brand/ClarityWordmark";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger } from
"@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger } from
"@/components/ui/tooltip";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/sonner";

import {
  ArrowRight,
  Check,
  Clock,
  ExternalLink,
  Lock,
  Shield,
  Sparkles,
  Eye,
  Calculator,
  Map,
  PlayCircle,
  Workflow,
  Plug,
  Layers,
  Bot,
  GitBranch,
  CreditCard,
  Menu,
  X
} from
"lucide-react";

import { useTheme } from "next-themes";

export const DEFAULT_SITE_COPY = {
  hero_headline: "Multiply productivity—without adding headcount.",
  hero_subhead:
    "The real leverage isn't another AI tool—it's AI agents orchestrated across the systems you already run. We help you install workflow automation that reads, decides, and acts with the right approvals, so you get more output without more overhead.",

  // CTA labels
  cta_primary: "Get My AI Opportunity Score",
  cta_secondary: "Book FREE Assessment Call",
  hero_support_text: "Get your top 3 workflow opportunities, readiness snapshot, and recommended next step.",
  hero_trust_text: "Fast response from a consultant-led team.",

  // Security section
  security_title: "Don't Risk Safety and Security",
  security_items: [
    "Only the right access — nothing more",
    "A clear record of everything the agent did",
    "You decide what gets automated (and what never does)",
    "Sensitive data stays protected",
    "Safe integrations, monitored like a production system",
  ],
  security_highlight: "No production-impacting automation without defined approval rules.",
  security_support: "Least privilege, audit trails, approval gates, and monitored integrations.",

  // Agent Orchestration section
  agent_orch_title: "Agent Orchestration + Business Systems Integration",
  agent_orch_platforms: ["OpenClaw", "Computer", "Manas"],
  agent_orch_intro: "Where AI creates value is not in isolated prompts, but in orchestrated workflows across the systems you already run. We specialize in agent orchestration—so your agents operate reliably inside your real operating environment.",
  agent_orch_subtitle: "What that means for your business",
  agent_orch_benefits: [
    {
      title: "Orchestrated workflows, not isolated prompts",
      desc: "We design agents as process engines—multi-step sequences with handoffs, exception handling, and clear ownership.",
      icon: "workflow"
    },
    {
      title: "Deep integration with your business systems",
      desc: "Agents pull context and push outcomes through approved integrations and APIs—tickets, records, drafts, requests, follow-ups.",
      icon: "plug"
    },
    {
      title: "Production-grade controls built in",
      desc: "Human approvals where it matters, least-privilege permissions, audit trails, and kill switches.",
      icon: "shield"
    },
    {
      title: "Visibility into outcomes and agent activity",
      desc: "Clear view of what agents did, what they're doing, and what's stuck—plus measurable outcomes for leadership.",
      icon: "eye"
    },
    {
      title: "Platform-agnostic implementation",
      desc: "We select the right orchestration pattern based on your systems, risk posture, and ROI target.",
      icon: "layers"
    }
  ],

  // How we do it section
  methodology_title: "How do we do it?",
  methodology_subtitle: "Discover → Assess → Implement → Expand",

  // Pricing
  pricing_title: "Pricing & Start",
  pricing_intro: "Most clients begin with the free Preliminary Assessment. We recommend the next step based on fit, urgency, and workflow readiness.",

  // Calendly
  calendly_url: "",

  // FAQ — see /pages/public/faqEntries.js for the master list.
  faq: [
    { q: "How can AI improve my business operations?",
      a: "AI improves business operations by reducing repetitive work, improving decision-making, increasing visibility into workflows, and helping teams execute faster. At 589 Labs, we identify the specific operational workflows where AI can create measurable impact — then build a practical roadmap to implement and measure results." },
    { q: "What are the best AI use cases for CEOs?",
      a: "The best AI use cases for CEOs are typically operational, not experimental. High-impact areas often include customer service, lead generation, reporting, workflow automation, knowledge management, forecasting, and internal communication. 589 Labs helps leadership teams prioritize the top 3 AI opportunities with the highest ROI potential." },
    { q: "How do I know where to start with AI?",
      a: "Most organizations struggle because they start with tools instead of strategy. The best starting point is an industry-specific AI assessment that identifies operational pain points, inefficiencies, and opportunities where AI can deliver measurable business value quickly." },
    { q: "What is an AI agent?",
      a: "An AI agent is software that can perform tasks autonomously using artificial intelligence. Unlike a chatbot, an AI agent can take actions, follow workflows, retrieve information, communicate with systems, and complete multi-step processes with limited human intervention." },
    { q: "How can AI agents help my company?",
      a: "AI agents can automate repetitive workflows, support customer interactions, summarize information, generate reports, monitor systems, and help teams execute faster. The most successful implementations focus on practical business workflows tied directly to KPIs and operational outcomes." },
    { q: "What departments benefit most from AI?",
      a: "Departments that benefit most from AI often include operations, sales, customer service, HR, finance, marketing, and executive leadership. The best opportunities are usually areas with repetitive work, slow response times, manual reporting, or disconnected systems." },
    { q: "How much does AI implementation cost?",
      a: "AI implementation costs vary depending on scope, systems, and complexity. Many organizations begin with a focused assessment and implementation roadmap before expanding into larger initiatives. 589 Labs focuses on practical, phased implementations designed to deliver measurable ROI quickly." },
    { q: "How long does it take to implement AI?",
      a: "Simple AI workflows can often be implemented within weeks, while broader operational transformations may take several months. The fastest successes usually come from focusing on 3 high-impact workflows first rather than trying to transform the entire organization at once." },
    { q: "What ROI should I expect from AI?",
      a: "AI ROI is typically measured through time savings, operational efficiency, revenue growth, improved customer experience, reduced manual work, and faster decision-making. At 589 Labs, every implementation includes KPI tracking to ensure AI initiatives are tied directly to business outcomes." },
    { q: "What are the risks of AI for businesses?",
      a: "The biggest risks are poor implementation, lack of governance, disconnected tools, weak adoption, and unclear business objectives. Successful AI programs require clear ownership, measurable goals, and practical workflows that teams can realistically adopt." },
    { q: "Do small businesses need AI?",
      a: "Small businesses may benefit even more from AI because they often operate with lean teams and limited resources. AI can help smaller organizations automate work, improve consistency, increase productivity, and scale without adding unnecessary overhead." },
    { q: "How do I create an AI strategy?",
      a: "An effective AI strategy starts by identifying where AI can create measurable business value. That includes assessing workflows, prioritizing opportunities, defining KPIs, assigning ownership, and building a phased execution plan tied to operational goals." },
    { q: "What is a Chief AI Officer?",
      a: "A Chief AI Officer (CAIO) is responsible for guiding AI strategy, governance, implementation, and business adoption across the organization. Many companies now use fractional or outsourced CAIO services to gain executive AI leadership without hiring a full-time executive." },
    { q: "How do I prepare my team for AI?",
      a: "Preparing teams for AI requires communication, training, practical workflows, and clear leadership support. The goal is not replacing employees — it is helping teams work faster, make better decisions, and focus on higher-value work." },
    { q: "What does a successful AI transformation look like?",
      a: "Successful AI transformations are measured by business outcomes, not hype. Organizations succeed when AI becomes part of daily operations, workflows become faster and more efficient, teams adopt the tools, and leadership can clearly measure operational improvements over time." }
  ]
};

export const useSiteCopy = () => {
  const [copy, setCopy] = useState(DEFAULT_SITE_COPY);
  const [loadingCopy, setLoadingCopy] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const doc = await siteCopyApi.get();
        const c = doc?.content || {};
        if (!mounted) return;
        setCopy({
          ...DEFAULT_SITE_COPY,
          ...c,
          faq: Array.isArray(c.faq) ? c.faq : DEFAULT_SITE_COPY.faq
        });
      } catch {

        // keep defaults
      } finally {if (mounted) setLoadingCopy(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  return { copy, loadingCopy };
};

export const Wordmark = () => {
  return (
    <div className="flex items-center" data-testid="brand-wordmark">
      <ClarityWordmark height={35} />
    </div>
  );
};

export const ThemeToggle = () => {
  const { theme, setTheme } = useTheme();
  const isNight = theme === "dark";
  const isLight = theme === "light";

  return (
    <button
      type="button"
      onClick={() => setTheme(isNight ? "light" : "dark")}
      className={[
        "group inline-flex items-center gap-2 rounded-full border px-3 py-2 text-sm backdrop-blur-xl transition-colors",
        isLight
          ? "border-border bg-white/70 text-foreground hover:bg-white"
          : "border border-border/60 dark:border-white/10 bg-background/60 dark:bg-white/5 text-foreground/80 dark:text-white/80 hover:bg-muted/60 dark:bg-white/10"
      ].join(" ")}
      aria-label="Toggle day/night theme">

      <span className="h-2 w-2 rounded-full bg-[var(--brand)] shadow-[0_0_18px_rgba(255,122,51,0.22)]" />
      <span className="hidden sm:inline">
        {isNight ? "Night" : "Day"}
      </span>
      <span className={isLight ? "text-foreground/50" : "text-foreground/50 dark:text-white/50"}>·</span>
      <span
        className={
          isLight
            ? "text-foreground/70 group-hover:text-foreground"
            : "text-foreground/70 dark:text-white/70 group-hover:text-foreground/85 dark:text-white/85"
        }
      >
        Toggle
      </span>
    </button>);

};

const scrollToSection = (id, navigate, pathname) => {
  const doScroll = () => {
    const el = document.getElementById(id);
    if (el) {
      const headerOffset = 90;
      const y = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };
  if (pathname === "/") {
    doScroll();
  } else {
    navigate("/");
    setTimeout(doScroll, 500);
  }
};

export const Nav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close the mobile panel on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const nav = [
    { to: "/services", label: "Services" },
    { to: "/#industries-section", label: "Industries" },
    { to: "/#how-it-works", label: "How it works" },
    { to: "/#pricing-section", label: "Pricing" },
    { to: "/about", label: "About" },
    { to: "/faq", label: "FAQ" },
    { to: "/contact", label: "Contact" },
  ];

  const handleMobileNav = (item) => {
    setMobileOpen(false);
    if (item.to.startsWith("/#")) {
      const sectionId = item.to.slice(2);
      scrollToSection(sectionId, navigate, location.pathname);
    } else {
      navigate(item.to);
    }
  };

  return (
    <header className="sticky top-0 z-50 border-b border-[#e5e7eb] bg-white/95 backdrop-blur-sm">
      <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-6 px-4 py-4 sm:px-6 lg:px-10">
        <Link to="/" className="focus:outline-none" data-testid="nav-logo-link">
          <Wordmark />
        </Link>

        <nav className="hidden items-center gap-7 lg:flex">
          {nav.map((n) => {
            if (n.to.startsWith("/#")) {
              const sectionId = n.to.slice(2);
              return (
                <button
                  key={n.to}
                  onClick={() => scrollToSection(sectionId, navigate, location.pathname)}
                  className="text-[14px] font-medium text-[#111827] hover:text-[#1a56db] transition-colors"
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {n.label}
                </button>
              );
            }
            return (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `text-[14px] font-medium transition-colors ${isActive ? "text-[#1a56db]" : "text-[#111827] hover:text-[#1a56db]"}`
                }
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="flex items-center gap-3 sm:gap-4">
          <div className="hidden sm:block h-5 w-px bg-[#e5e7eb]" />
          <Link to="/app" data-testid="nav-signin-link" className="hidden sm:inline-flex">
            <button className="text-[14px] font-medium text-[#9ca3af] hover:text-[#111827] transition-colors">
              Sign in
            </button>
          </Link>
          <Button
            onClick={() => {
              trackEvent("booked_call", { source: "nav_cta" });
              analyticsApi.track("booked_call", { source: "nav_cta" });
              navigate("/contact?intent=book");
              setTimeout(() => window.scrollTo({ top: 0, behavior: "instant" }), 100);
            }}
            className="hidden md:inline-flex h-10 rounded-lg bg-[#1a56db] px-5 text-[14px] font-semibold text-white hover:bg-[#143faa] shadow-none"
            data-testid="nav-book-assessment-btn"
          >
            Book Free Assessment
          </Button>
          <button
            type="button"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((o) => !o)}
            className="inline-flex lg:hidden h-10 w-10 items-center justify-center rounded-lg border border-[#e5e7eb] text-[#111827] hover:bg-[#f9fafb] transition-colors"
            data-testid="nav-mobile-toggle"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div
          className="lg:hidden border-t border-[#e5e7eb] bg-white"
          data-testid="nav-mobile-panel"
        >
          <nav className="mx-auto flex w-full max-w-7xl flex-col gap-1 px-4 py-4 sm:px-6">
            {nav.map((n) => (
              <button
                key={n.to}
                onClick={() => handleMobileNav(n)}
                className="text-left text-[15px] font-medium text-[#111827] px-3 py-3 rounded-lg hover:bg-[#f9fafb] transition-colors"
                data-testid={`nav-mobile-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </button>
            ))}
            <div className="mt-2 pt-3 border-t border-[#e5e7eb] flex flex-col gap-2">
              <Link
                to="/app"
                className="text-[14px] font-medium text-[#4b5563] px-3 py-3 rounded-lg hover:bg-[#f9fafb]"
                data-testid="nav-mobile-signin"
                onClick={() => setMobileOpen(false)}
              >
                Sign in
              </Link>
              <Button
                onClick={() => {
                  setMobileOpen(false);
                  trackEvent("booked_call", { source: "nav_cta_mobile" });
                  analyticsApi.track("booked_call", { source: "nav_cta_mobile" });
                  navigate("/contact?intent=book");
                  setTimeout(() => window.scrollTo({ top: 0, behavior: "instant" }), 100);
                }}
                className="h-11 rounded-lg bg-[#1a56db] text-[14px] font-semibold text-white hover:bg-[#143faa] shadow-none"
                data-testid="nav-mobile-book-btn"
              >
                Book Free Assessment
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export const Footer = () => {
  return (
    <footer className="border-t border-[#e5e7eb] bg-white" data-testid="site-footer">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 px-4 py-8 sm:px-6 lg:px-10 md:flex-row md:items-center md:justify-between">
        <p className="text-sm text-[#9ca3af]">
          &copy; {new Date().getFullYear()} 589 Labs.ai. All rights reserved.
        </p>
        <nav className="flex items-center gap-7 text-sm">
          <Link className="text-[#4b5563] hover:text-[#111827] transition-colors" to="/about">About</Link>
          <Link className="text-[#4b5563] hover:text-[#111827] transition-colors" to="/contact">Contact</Link>
          <Link className="text-[#4b5563] hover:text-[#111827] transition-colors" to="/privacy">Privacy</Link>
          <Link className="text-[#4b5563] hover:text-[#111827] transition-colors" to="/terms">Terms</Link>
        </nav>
      </div>
    </footer>
  );
};

export const PageShell = ({ children }) => {
  return (
    <div className="min-h-screen bg-white">
      <Nav />
      <SplashVideoOverlay />
      <main className="w-full">
        {children}
      </main>
      <Footer />
      <ClawAssistantWidget />
    </div>);

};

export const ResponseBadge = () => {
  return (
    <div className="inline-flex w-fit items-center gap-2 rounded-full border border-border/60 dark:border-white/10 bg-background/60 dark:bg-white/5 px-3 py-2 text-sm text-foreground/70 dark:text-white/70 backdrop-blur-xl">
      <Clock className="h-4 w-4 text-[var(--brand)]" />
      Fast response from a <span className="text-foreground dark:text-white">consultant-led team</span>
    </div>);

};

export const PrimaryCTA = ({ className = "", copy = DEFAULT_SITE_COPY }) => {
  const navigate = useNavigate();

  return (
    <div
      className={[
      "rounded-2xl border border-border/60 dark:border-white/10 bg-background/60 dark:bg-white/5 p-5 backdrop-blur-2xl",
      "shadow-[0_24px_90px_rgba(0,0,0,0.55)]",
      className].
      join(" ")}>

      <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
        <div>
          <div className="flex items-center gap-2 text-foreground/80 dark:text-white/80">
            <Sparkles className="h-4 w-4 text-[var(--brand)]" />
            <span className="text-sm">Start with a quick assessment</span>
          </div>
          <div className="mt-1 text-lg font-semibold tracking-tight text-foreground dark:text-white">
            {copy.hero_support_text || DEFAULT_SITE_COPY.hero_support_text}
          </div>
        </div>

        <div className="flex w-full flex-col gap-2 md:w-full lg:w-auto lg:flex-row lg:flex-wrap">
          <Button
            onClick={() => {
              trackEvent("form_submit", { form: "opportunity_score", source: "public_cta" });
              analyticsApi.track("form_submit", { form: "opportunity_score", source: "public_cta" });
              const el = document.getElementById("opportunity-score");
              if (el) {
                const yOffset = -100; // account for sticky header
                const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
                window.scrollTo({ top: y, behavior: "smooth" });
              }
            }}
            className="h-12 w-full rounded-none bg-[var(--brand)] px-5 text-white transition-transform hover:scale-[1.02] hover:bg-[var(--brand)] sm:w-auto lg:flex-1 lg:min-w-[320px] cta-pulse">

            {copy.cta_primary || DEFAULT_SITE_COPY.cta_primary}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              trackEvent("booked_call", { source: "public_cta" });
              analyticsApi.track("booked_call", { source: "public_cta" });
              navigate("/contact?intent=book");
              setTimeout(() => window.scrollTo({ top: 0, behavior: "instant" }), 100);
            }}
            className="h-12 w-full rounded-none border border-border/60 dark:border-white/10 bg-background/60 dark:bg-white/5 text-foreground dark:text-white hover:bg-white hover:text-foreground sm:w-auto lg:flex-1 lg:min-w-[260px]">

            {copy.cta_secondary || DEFAULT_SITE_COPY.cta_secondary}
            <ExternalLink className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>);

};

export const VerticalSwitcher = ({ value, onChange }) => {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-xs uppercase tracking-wide text-foreground/55 dark:text-white/55">
        Pick your industry
      </div>
      <div className="flex flex-wrap gap-2">
        {INDUSTRIES.map((v) => {
          const active = v.key === value;
          return (
            <button
              key={v.key}
              type="button"
              onClick={() => onChange(v.key)}
              className={[
              "rounded-full border px-3 py-2 text-sm backdrop-blur-xl transition-colors",
              active ?
              "border-border/60 dark:border-white/20 bg-muted/60 dark:bg-white/10 text-foreground dark:text-white" :
              "border-border/60 dark:border-white/10 bg-background/60 dark:bg-white/5 text-foreground/65 dark:text-white/65 hover:bg-muted/60 dark:bg-white/10 hover:text-foreground dark:hover:text-white"].
              join(" ")}>

              {v.label}
            </button>);

        })}
      </div>
    </div>);

};

export const ThreeBlocks = ({ verticalKey, getIndustryContent }) => {
  const content = getIndustryContent ?
  getIndustryContent(verticalKey) :
  INDUSTRY_CONTENT[verticalKey];
  return (
    <div className="grid gap-4 md:grid-cols-3">
      <Card className="rounded-2xl border-border/60 dark:border-white/10 bg-background/60 dark:bg-white/5 p-5 text-foreground/85 dark:text-white/85 backdrop-blur-2xl">
        <div className="flex items-center gap-2 text-sm text-foreground/80 dark:text-white/80">
          <Sparkles className="h-4 w-4 text-[var(--brand)]" />
          Top 3 use cases
        </div>
        <ul className="mt-4 space-y-2 text-sm text-foreground/70 dark:text-white/70">
          {content.use_cases.map((x) =>
          <li key={x} className="flex gap-2">
              <Check className="mt-0.5 h-4 w-4 text-[var(--brand)]" />
              <span>{x}</span>
            </li>
          )}
        </ul>
      </Card>

      <Card className="rounded-2xl border-border/60 dark:border-white/10 bg-background/60 dark:bg-white/5 p-5 text-foreground/85 dark:text-white/85 backdrop-blur-2xl">
        <div className="flex items-center gap-2 text-sm text-foreground/80 dark:text-white/80">
          <ArrowRight className="h-4 w-4 text-[var(--brand)]" /> KPI metrics - outcomes
        </div>
        <ul className="mt-4 space-y-2 text-sm text-foreground/70 dark:text-white/70">
          {content.kpis.map((x) =>
          <li key={x} className="flex gap-2">
              <Check className="mt-0.5 h-4 w-4 text-[var(--brand)]" />
              <span>{x}</span>
            </li>
          )}
        </ul>
      </Card>

      <Card className="rounded-2xl border-border/60 dark:border-white/10 bg-background/60 dark:bg-white/5 p-5 text-foreground/85 dark:text-white/85 backdrop-blur-2xl">
        <div className="flex items-center gap-2 text-sm text-foreground/80 dark:text-white/80">
          <Shield className="h-4 w-4 text-[var(--brand)]" />
          Risk & security
        </div>
        <ul className="mt-4 space-y-2 text-sm text-foreground/70 dark:text-white/70">
          {content.risks.map((x) =>
          <li key={x} className="flex gap-2">
              <Lock className="mt-0.5 h-4 w-4 text-foreground/60 dark:text-white/60" />
              <span>{x}</span>
            </li>
          )}
        </ul>
      </Card>
    </div>);

};
